import type { ButtonHTMLAttributes, ReactNode } from 'react';

type CpmIconButtonSize = 'xs' | 'sm' | 'md';
type CpmIconButtonTone = 'brand' | 'danger' | 'success';

interface CpmIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    icon: ReactNode;
    label: string;
    size?: CpmIconButtonSize;
    tone?: CpmIconButtonTone;
    active?: boolean;
}

const SIZES: Record<CpmIconButtonSize, number> = {
    xs: 20,
    sm: 26,
    md: 30,
};

const TONES: Record<CpmIconButtonTone, string> = {
    brand: 'var(--cpm-brand-purple)',
    danger: 'var(--cpm-shortfall-icon, var(--cpm-shortfall-text))',
    success: 'var(--cpm-success-text-light)',
};

/**
 * Design-system IconButton — `_ds_bundle.js` sizes xs/sm/md (20/26/30),
 * 1px border, white fill, purple accent on hover.
 */
export default function CpmIconButton({
    icon,
    label,
    size = 'md',
    tone = 'brand',
    active = false,
    disabled = false,
    className = '',
    style,
    ...props
}: CpmIconButtonProps) {
    const px = SIZES[size];
    const accent = TONES[tone];
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            disabled={disabled}
            className={[
                'inline-flex items-center justify-center shrink-0 transition-colors duration-150',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                className,
            ].join(' ')}
            style={{
                width: px,
                height: px,
                padding: 0,
                flex: 'none',
                borderRadius: px <= 20 ? 'var(--cpm-radius-5)' : 'var(--cpm-radius-8)',
                border: `1px solid ${active ? accent : 'var(--cpm-border-default)'}`,
                background: '#fff',
                color: active ? accent : 'var(--cpm-text-secondary)',
                ...style,
            }}
            onMouseEnter={(event) => {
                if (disabled) return;
                event.currentTarget.style.borderColor = accent;
                event.currentTarget.style.color = accent;
            }}
            onMouseLeave={(event) => {
                if (active) return;
                event.currentTarget.style.borderColor = 'var(--cpm-border-default)';
                event.currentTarget.style.color = 'var(--cpm-text-secondary)';
            }}
            {...props}
        >
            {icon}
        </button>
    );
}
